import { styled } from '@mui/material/styles'
import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { floatActionsPositionStyle } from '#/components/FloatActions/Logic/floatActionsPosition'
import { useFloatActionsDrag } from '#/components/FloatActions/Logic/useFloatActionsDrag'
import { useCanUseVicChat } from '#/components/VicChat/Logic/useCanUseVicChat'
import { useFarmSlug } from '#/store/ducks/farm'
import { isBusinessEnvironment } from '#/utils'
import { getLink } from '#/utils/getLink'
import lazyRetry from '#/utils/lazyRetry'

const Wrapper = styled('div', {
  shouldForwardProp: prop => prop !== 'isDragging',
})<{ isDragging: boolean }>(({ isDragging, theme }) => ({
  'display': 'flex',
  'flexDirection': 'row-reverse',
  'gap': 10,
  'position': 'fixed',
  'touchAction': 'none',
  'userSelect': 'none',
  'zIndex': theme.zIndex.speedDial,
  ...(isDragging && {
    '& *': { cursor: 'grabbing' },
    'cursor': 'grabbing',
  }),
}))

const QuickActions = React.lazy(() => lazyRetry(() => import('#/components/QuickActions')))
const Routine = React.lazy(() => lazyRetry(() => import('#/components/Routine')))
const VicChatFab = React.lazy(() => lazyRetry(() => import('#/components/VicChat/Views/Fab')))

export default function FloatActions() {
  const { pathname } = useLocation()
  const canUseVicChat = useCanUseVicChat()
  const { elementRef, handlers, isDragging, position } = useFloatActionsDrag()

  const farmSlug = useFarmSlug()
  const isB2B = isBusinessEnvironment()

  const actions = useMemo(
    () => [
      {
        Component: VicChatFab,
        enabled: canUseVicChat,
        key: 'vic-chat',
      },
      {
        Component: Routine,
        enabled: pathname !== getLink({ id: 'farm-vic-routines' }),
        key: 'vic-routine',
      },
      {
        Component: QuickActions,
        enabled: Boolean(pathname.match(`^/farm/${farmSlug}/?$`)),
        key: 'quick-actions',
      },
    ],
    [canUseVicChat, farmSlug, pathname],
  )

  return !isB2B ? (
    <Wrapper
      ref={elementRef}
      isDragging={isDragging}
      style={floatActionsPositionStyle(position)}
      {...handlers}
    >
      {actions.map(({ Component, enabled, key }) => enabled && <Component key={key} />)}
    </Wrapper>
  ) : null
}
